import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Container, Text, Flex, VStack, GridItem } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { DeptGrid } from '@repo/ui-components/dept/design-system';
import Link from '@repo/ui-components/tailwind/Link';
import { generateSectionId } from '../../../utils/generateSectionId';
import { SanityBackgroundImage } from '../../../atoms';

function ProdGridImage({ block }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!block?.collections) return null;
  return (
    <Container pt={[4, null, 20]} pb={4} {...generateSectionId(block)}>
      <DeptGrid w="full">
        {block.collections.map((collection, index) => {
          const href = {
            linkType: 'internal',
            href: null,
            internalLink: collection.collectionReference,
          };
          return (
            <GridItem
              key={collection._key}
              colSpan={{
                base: index === block.collections.length - 1 ? 6 : 3,
                lg: 4,
              }}
            >
              <VStack
                as={Link}
                className="relative flex flex-col items-start"
                key={collection._key}
                link={collection?.cta || href}
                sx={{
                  '& img': {
                    transition: 'transform .3s ease-in-out',
                  },
                  '&:hover img': {
                    transform: 'scale(1.05)',
                  },
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <SanityBackgroundImage
                  as={Flex}
                  image={
                    collection.media?.image ??
                    collection.collectionReference?.image
                  }
                  aspectRatio={{
                    base: index === block.collections.length - 1 ? 16 / 8 : 1,
                    lg: 1,
                  }}
                  borderRadius={['lg', null, '8px 40px']}
                  gradientGenerator={url =>
                    `linear-gradient(180deg, rgb(0 0 0 / 50%) 0%, rgba(0, 0, 0, 0.00) 100%), url(${url}), #d3d3d31a -366.622px -10px / 204.33% 136.237% no-repeat`
                  }
                  width="100%"
                  height="100%"
                  py={[4, null, 8]}
                  px={[4, null, 10]}
                  flexDirection="column"
                >
                  <Text
                    color="typography.lighter !important"
                    as="h2"
                    textStyle={['h3', null, 'h2']}
                    pb={2}
                  >
                    {collection.title}
                  </Text>
                  <motion.div
                    variants={{
                      hover: { width: '100%' },
                      noHover: { width: 0 },
                    }}
                    initial="noHover"
                    animate={hoveredIndex === index ? 'hover' : 'noHover'}
                    style={{ height: '5px', background: '#FFEB00' }}
                  />
                </SanityBackgroundImage>
              </VStack>
            </GridItem>
          );
        })}
      </DeptGrid>
    </Container>
  );
}

ProdGridImage.propTypes = {
  block: PropTypes.shape({
    collections: PropTypes.arrayOf(
      PropTypes.shape({
        _key: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        media: PropTypes.shape({
          image: PropTypes.shape({
            asset: PropTypes.shape({
              url: PropTypes.string,
            }),
            alt: PropTypes.string,
          }),
        }),
        collectionReference: PropTypes.shape({
          _type: PropTypes.string,
          image: PropTypes.shape({
            asset: PropTypes.shape({
              url: PropTypes.string,
            }),
            alt: PropTypes.string,
          }),
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
          store: PropTypes.shape({
            slug: PropTypes.shape({
              current: PropTypes.string,
            }),
          }),
        }),
      })
    ),
  }).isRequired,
};

export default ProdGridImage;
